import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

/**
 * OptionsCarousel — envuelve las tarjetas de opciones y agrega flechas
 * cuando el contenido se pasa del ancho disponible (mismo comportamiento que el POS).
 *
 * Uso:  <OptionsCarousel>{listItems}</OptionsCarousel>
 *
 * @param {number} [step=200]   px que avanza cada flecha
 * @param {any}   [deps]        valor que, al cambiar, recalcula las flechas (ej. segment.segment_id)
 */
export function OptionsCarousel({ children, step = 200, deps, className = '' }) {
  const trackRef = useRef(null);
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(false);

  const update = useCallback(() => {
    const el = trackRef.current;
    if (!el) return;
    const overflow = el.scrollWidth - el.clientWidth > 4;
    setCanPrev(overflow && el.scrollLeft > 4);
    setCanNext(overflow && el.scrollLeft < el.scrollWidth - el.clientWidth - 4);
  }, []);

  useLayoutEffect(() => { update(); }, [children, deps, update]);

  useEffect(() => {
    const el = trackRef.current;
    if (!el) return;
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    window.addEventListener('resize', update);
    return () => { ro.disconnect(); window.removeEventListener('resize', update); };
  }, [update]);

  const scroll = (dir) => trackRef.current?.scrollBy({ left: dir * step, behavior: 'smooth' });

  return (
    <div className={`pp-carousel ${className}`.trim()}>
      <button
        type="button"
        className={`pp-carousel__arrow pp-carousel__arrow--left ${canPrev ? 'is-visible' : ''}`}
        onClick={() => scroll(-1)} aria-label="Anterior" tabIndex={-1}
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><polyline points="15 18 9 12 15 6" /></svg>
      </button>

      <div className="pp-carousel__track" ref={trackRef} onScroll={update}>
        {children}
      </div>

      <button
        type="button"
        className={`pp-carousel__arrow pp-carousel__arrow--right ${canNext ? 'is-visible' : ''}`}
        onClick={() => scroll(1)} aria-label="Siguiente" tabIndex={-1}
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><polyline points="9 18 15 12 9 6" /></svg>
      </button>
    </div>
  );
}

export default OptionsCarousel;